<script setup lang="ts">
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useExplorerStore } from "@/modules/explorer/store";
import DocumentViewTabs = App.Enums.DocumentViewTabs;

const route = useRoute();
const router = useRouter();

const tabs = ref([
	{
		itemId: "graph",
		translationKey: "Graph",
		icon: "fa-chart-column",
		to: `/explorer/graph`,
		disabled: false,
	},
	{
		itemId: "table",
		translationKey: "Data table",
		icon: "fa-table-layout",
		to: `/explorer/metrics`,
		disabled: false,
	},
	{
		itemId: "single-metric",
		translationKey: "Single metric",
		icon: "fa-gauge-high",
		to: `/explorer/single-metric`,
		disabled: false,
	},
]);

const setActiveDataView = (viewTab: DocumentViewTabs) => {
	const store = useExplorerStore();
	if (store.explorerState.activeDataView !== viewTab) {
		store.explorerState.activeDataView = viewTab;
	}
};

// Keep the active tab in sync with the current route (deep links / refresh).
watch(
	() => route.path,
	(path) => {
		const tab = tabs.value.find((t) => t.to === path);
		if (tab) {
			setActiveDataView(tab.itemId as DocumentViewTabs);
		}
	},
	{ immediate: true }
);

// User switches tabs: navigate to the matching route and persist the choice on
// the document so it is saved alongside the rest of the document.
watch(
	() => useExplorerStore().explorerState.activeDataView,
	(activeDataView) => {
		const store = useExplorerStore();
		if (store.document && store.document.view_tab !== activeDataView) {
			store.document.view_tab = activeDataView;
		}

		const tab = tabs.value.find((t) => t.itemId === activeDataView);
		if (tab && route.path !== tab.to) {
			router.push(tab.to);
		}
	}
);

// Opening a document activates the tab it was saved with,
// which navigates to the matching route via the watcher above.
watch(
	() => useExplorerStore().document?.view_tab,
	(viewTab) => {
		if (viewTab) {
			setActiveDataView(viewTab);
		}
	},
	{ immediate: true }
);
</script>

<template>
	<v-tabs
		class="condensed-tab-height mt-0 rounded"
		density="compact"
		v-model="useExplorerStore().explorerState.activeDataView"
	>
		<v-tab
			v-for="tab in tabs"
			:key="tab.itemId"
			:value="tab.itemId"
			color="primary"
			:disabled="tab.disabled"
			:prepend-icon="`fa: fa-regular ${tab.icon}`"
		>
			<div class="text-caption">
				{{ $t(tab.translationKey) }}
			</div>
		</v-tab>
	</v-tabs>
</template>

<style lang="scss" scoped>
.condensed-tab-height {
	--v-tabs-height: 29px;
}
</style>
